
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Sidebar from '@/components/layout/Sidebar';
import Footer from '@/components/layout/Footer';
import { useAuthSession } from '@/hooks/useAuthSession';
import { useSubscription } from '@/hooks/useSubscription';
import { TrialExpirationBanner } from '@/components/subscription/TrialExpirationBanner';
import { PromoCountdownBanner } from '@/components/subscription/PromoCountdownBanner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { FreePlanBanner } from '@/components/subscription/FreePlanBanner';
import { checkFreePlanExpiry } from '@/utils/freePlanHelpers';

const MainLayout: React.FC<{ children: React.ReactNode; loading?: boolean }> = ({ children, loading = false }) => {
  const { isAuthenticated } = useAuthSession();
  const { subscription } = useSubscription();
  const { isMobile, isTablet, screenSize } = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [freePlanDaysLeft, setFreePlanDaysLeft] = useState<number | null>(null);
  const isExtraSmallScreen = screenSize === 'xs';
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const checkFreePlan = async () => {
      if (isAuthenticated && subscription?.has_active_subscription && subscription?.subscription?.plan === 'Free') {
        const result = await checkFreePlanExpiry();
        setFreePlanDaysLeft(result?.daysRemaining ?? null);
      } else {
        setFreePlanDaysLeft(null);
      }
    };
    checkFreePlan();
  }, [isAuthenticated, subscription]);

  const calculateDaysLeft = () => {
    if (!subscription?.subscription?.is_trial || !subscription?.subscription?.trial_ends_at) return -1;
    const trialEnd = new Date(subscription.subscription.trial_ends_at);
    const today = new Date();
    const diff = Math.ceil((trialEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const daysLeft = calculateDaysLeft();
  const isOnTrial = subscription?.subscription?.is_trial;
  const isOnFreePlan = subscription?.subscription?.plan === 'Free';
  const isPro = subscription?.subscription?.plan?.toLowerCase() === 'pro';
  
  // Check if promotional period is active
  const isPromoActive = new Date() < new Date('2025-06-30T23:59:59');
  
  // Debug logging
  console.log('MainLayout Debug:', {
    isAuthenticated,
    isPro,
    isPromoActive,
    subscription: subscription?.subscription
  });

  // Additional debug logging for promo banner visibility
  if (isAuthenticated && !isPro && isPromoActive) {
    console.log('Promotional Banner should be visible now');
  }

  if (loading) return <LoadingScreen centered fullscreen withCard={false} message="Loading application..." />;

  // Determine content padding based on screen size
  const contentPadding = (() => {
    if (isExtraSmallScreen) return 'px-1';
    if (isMobile) return 'px-2 sm:px-3';
    return 'px-3 sm:px-4 md:px-6';
  })();

  const contentMargin = isMobile ? 'mb-16' : ''; // Add bottom margin on mobile for nav bar

  return (
    <div className="flex min-h-screen flex-col bg-background transition-colors duration-300 antialiased">
      {/* Promotional Banner - Show to all users except Pro */}
      {isAuthenticated && !isPro && isPromoActive && (
        <PromoCountdownBanner />
      )}
      
      <Navbar
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        showMobileMenuButton={isAuthenticated && isMobile}
      />

      <div className="flex flex-1">
        {!isMobile && isAuthenticated && <Sidebar />}

        {isMobile && isAuthenticated && (
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetContent
              side="left"
              className={`p-0 ${isTablet ? 'w-[320px] max-w-[350px]' : 'w-[80%] max-w-[280px]'} z-50 transition-transform duration-300 ease-in-out`}
            >
              <Sidebar isMobile onMobileClose={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
        )}

        <main className={`flex-1 ${contentPadding} py-2 sm:py-4 bg-background transition duration-300 w-full overflow-x-hidden ${contentMargin}`}>
          <div className={`mx-auto w-full ${isExtraSmallScreen ? 'max-w-full' : 'max-w-7xl'}`}>
            {(isOnTrial || (isOnFreePlan && freePlanDaysLeft !== null)) && (
              <div className="sticky top-0 z-30 bg-white/70 backdrop-blur-md rounded-b-md shadow-sm mb-2">
                {isOnTrial && <TrialExpirationBanner daysLeft={daysLeft} />}
                {isOnFreePlan && freePlanDaysLeft !== null && <FreePlanBanner daysRemaining={freePlanDaysLeft} />}
              </div>
            )}
            <div className="transition-all duration-300 animate-fadeIn">
              {children}
            </div>
          </div>
        </main>
      </div>

      {!isMobile && <Footer />}
    </div>
  );
};

export default MainLayout;
